import { Button, Modal, Spinner } from "react-bootstrap";
import { useState } from "react";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import { CustomDifference } from "./interactiveFileTransfer";
import { InteractiveFileTransferImportFolders } from "./interactiveFileTransferImportFolders";
import Error from "../abstractComponents/error";
import { ExtendedRepositoryObject } from "../../backend/types";
import { InteractiveFileInput } from "../../backend/interactiveTypes";
import { useImportFilesMutation } from "../../backend/hooks/interactive/mutationImportFiles";

export function InteractiveFileTransferImportFoldersModal(props: {
  environmentId: string;
  setSuccessMessage: (message: { message: string; id: number } | null) => void;
  selectedFiles: CustomDifference[];
  setSelectedFiles: React.Dispatch<
    React.SetStateAction<CustomDifference[] | null>
  >;
}) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedFolders, setSelectedFolders] = useState<
    ExtendedRepositoryObject[]
  >([]);

  const { importFiles, isLoadingImportFiles, errorImportFiles } =
    useImportFilesMutation();

  const performImport = () => {
    if (selectedFolders.length === 0 && props.selectedFiles.length === 0)
      return;

    let importPaths: InteractiveFileInput[] = [];

    if (props.selectedFiles.length > 0) {
      props.selectedFiles.forEach((file: CustomDifference) => {
        importPaths.push({
          repoName: file.repoName,
          path: file.fileName
        });
      });
    }

    selectedFolders.forEach((folder: ExtendedRepositoryObject) => {
      importPaths.push({
        repoName: folder.repository.name,
        path: folder.absolutePath
      });
    });

    importFiles({
      variables: {
        environmentId: props.environmentId,
        importPaths
      }
    }).then(() => {
      setIsModalVisible(false);
      setSelectedFolders([]);
      props.setSelectedFiles([]);
      props.setSuccessMessage({
        message: "Importing files, please check the notification for progress.",
        id: Date.now()
      });
    });
  };

  return (
    <>
      <Button
        id="import-folders-button"
        className="button-secondary me-2"
        variant="primary"
        onClick={() => setIsModalVisible(true)}
      >
        <DriveFolderUploadIcon /> Import Folders
      </Button>
      <Modal
        show={isModalVisible}
        onHide={() => setIsModalVisible(false)}
        dialogClassName="modal-80w interactive_modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Import folders from PHIL into Interactive</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InteractiveFileTransferImportFolders
            environmentId={props.environmentId}
            setSuccessMessage={props.setSuccessMessage}
            selectedFolders={selectedFolders}
            setSelectedFolders={setSelectedFolders}
            onImportSuccess={() => setIsModalVisible(false)}
          />
          {props.selectedFiles && props.selectedFiles.length > 0 && (
            <div className="my-3">
              <p className="text-muted small">
                Preview of files from previous selection (showing up to 1000):
              </p>
              <div className="border p-2 selected-files-preview">
                <table className="table table-sm list-unstyled mb-0">
                  <thead>
                    <tr>
                      <th className="small">State</th>
                      <th className="small">File Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {props.selectedFiles.slice(0, 1000).map((file, index) => (
                      <tr key={index}>
                        <td className="small">{file.type}</td>
                        <td className="small">{file.key}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIsModalVisible(false)}>
            Cancel
          </Button>
          <Button
            data-testid="import-selected-folders-button"
            variant="primary"
            disabled={selectedFolders.length === 0 || isLoadingImportFiles}
            onClick={performImport}
          >
            {isLoadingImportFiles ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Importing...
              </>
            ) : (
              "Import Selected Folders"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      {errorImportFiles && <Error error={errorImportFiles} />}
    </>
  );
}
